﻿// src/modules/registry.ts

export type ModuleKey =
  | "pms"
  | "identity"
  | "identity-management"
  | "settings"
  | "pos"
  | "ems"
  | "voip"
  | "wms"
  | "revenue"
  | "guests"
  | "feedback"
  | "cctv"
  | "support"
  | "keycoder"
  | "garage"
  | "parking-preview";

export type ModuleKind = "internal" | "external" | "placeholder";

export type ModuleDefinition = {
  key: ModuleKey;
  title: string;
  subtitle?: string;
  enabled: boolean; // false = Coming Soon
  order: number;
  kind: ModuleKind;
  externalUrl?: string;
  path?: string;
  showOnDesktop?: boolean;
};

// "Parking Preview" is a local UX prototype on demo data, not the live Garage parking module. It
// stays reachable by direct URL (/module/parking-preview) but is only listed in the module
// navigation when VITE_SHOW_PARKING_PREVIEW_IN_NAV is enabled.
const SHOW_PARKING_PREVIEW_IN_NAV = ["1", "true", "yes", "on"].includes(
  String(import.meta.env?.VITE_SHOW_PARKING_PREVIEW_IN_NAV ?? "").trim().toLowerCase(),
);

/** Modules switched off for every user for now; their routes redirect to the Infoboard. */
export const HIDDEN_MODULE_KEYS: ReadonlySet<string> = new Set(["ems", "voip", "wms"]);

export const MODULES: ModuleDefinition[] = [
  { key: "pms", title: "PMS", enabled: true, order: 1, kind: "internal" },
  // Hidden from the sidebar/module nav — reachable only from the user menu
  // (click on the user's name) and by direct URL.
  { key: "identity", title: "Identity Manager", enabled: true, order: 1.5, kind: "internal", showOnDesktop: false },
  { key: "identity-management", title: "Identity Management", enabled: true, order: 1.6, kind: "internal", showOnDesktop: false },
  { key: "settings", title: "Settings", enabled: true, order: 1.75, kind: "internal", path: "/settings/overview", showOnDesktop: false },
  { key: "pos", title: "POS", enabled: true, order: 2, kind: "internal" },
  // EMS, VoIP and WMS are hidden for everyone for now (not ready): no navigation entry and their
  // routes redirect to the Infoboard. Remove them from HIDDEN_MODULE_KEYS to bring them back.
  { key: "ems", title: "EMS", enabled: true, order: 3, kind: "internal", showOnDesktop: false },
  { key: "voip", title: "VoIP", enabled: true, order: 4, kind: "internal", showOnDesktop: false },
  { key: "wms", title: "WMS", enabled: true, order: 5, kind: "internal", showOnDesktop: false },
  { key: "revenue", title: "Revenue Control", enabled: true, order: 6, kind: "internal" },
  { key: "guests", title: "Guests", enabled: true, order: 7, kind: "internal" },

  {
    key: "cctv",
    title: "CCTV",
    subtitle: "Security Control Center",
    enabled: true,
    order: 8,
    kind: "internal",
  },
  {
    // External Jira Service Desk portal (opens in a new tab); navigation labels it as external.
    key: "support",
    title: "Support",
    subtitle: "External Jira Service Desk portal",
    enabled: true,
    order: 9,
    kind: "external",
    externalUrl: "https://reustit-team.atlassian.net/servicedesk/customer/portals",
  },
  {
    key: "keycoder",
    title: "Keycoder",
    subtitle: "Access Control Center",
    enabled: true,
    order: 10,
    kind: "internal",
  },
  {
    key: "garage",
    title: "Garage",
    subtitle: "Parking Control Center",
    enabled: true,
    order: 11,
    kind: "internal",
  },
  {
    key: "parking-preview",
    title: "Parking Preview",
    subtitle: "Local parking UX prototype",
    enabled: true,
    order: 11.5,
    kind: "internal",
    showOnDesktop: SHOW_PARKING_PREVIEW_IN_NAV,
  },
  {
    // Feedback (replaced Surveys): staff opinions about the app for the Xentros team; open to every signed-in user.
    key: "feedback",
    title: "Feedback",
    subtitle: "Tell the Xentros team what you think about the app",
    enabled: true,
    order: 12,
    kind: "internal",
  },
];

export const MODULES_BY_KEY = MODULES.reduce((acc, module) => {
  acc[module.key] = module;
  return acc;
}, {} as Record<ModuleKey, ModuleDefinition>);

export function getModuleByKey(key: string): ModuleDefinition | null {
  return MODULES_BY_KEY[key as ModuleKey] ?? null;
}

export function isModuleKey(x: string): x is ModuleKey {
  return Boolean(MODULES_BY_KEY[x as ModuleKey]);
}
